import { searchStreetTypes, searchSuburbStatePostcodes } from '@mobi/account/Common/Address'

export const DELAY = 750

export const fetchSuggestedStreetTypes = debounceWithReturn(
  (inputValue: string): Promise<string[]> => {
    if (!inputValue || inputValue.length < 2) {
      return Promise.resolve<string[]>([])
    }

    return searchStreetTypes(inputValue).then(result => result.map(({ value }) => value))
  },
  DELAY
) as (inputValue: string) => Promise<string[]>

export const fetchSuggestedSuburbStatePostCodes = debounceWithReturn(
  (inputValue: string): Promise<string[]> => {
    if (!inputValue || inputValue.length < 4) {
      return Promise.resolve<string[]>([])
    }

    return searchSuburbStatePostcodes(inputValue).then(result => result.map(({ value }) => value))
  },
  DELAY
) as (inputValue: string) => Promise<string[]>

// =============
// Local Helpers
// =============

/**
 * Debounce a function and allow a value to be returned as a promise. The promise will be resolved after the wait time has expired.
 *
 * @param funcToDebounce The function that should be debounced.
 * @param delay The amount of time to wait before resolving the promise. Every call to the returned function will reset the countdown.
 */
export function debounceWithReturn<TFunc extends (...args: never[]) => unknown>(
  funcToDebounce: (...args: Parameters<TFunc>) => unknown,
  delay: number
): (...args: Parameters<TFunc>) => Promise<unknown> {
  let timeoutId: number | undefined = undefined

  const debouncedFunc = (...argsFromCall: Parameters<TFunc>): Promise<unknown> => {
    if (timeoutId != undefined) {
      clearTimeout(timeoutId)
      timeoutId = undefined
    }

    return new Promise<unknown>((resolve, reject) => {
      timeoutId = window.setTimeout(() => {
        try {
          resolve(funcToDebounce(...argsFromCall))
        } catch (error) {
          reject(error)
        }
      }, delay)
    })
  }

  return debouncedFunc
}
